import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LineTrend } from "@/components/Charts";
import { fmtCcy, verdictLabel } from "@/lib/format";
import {
  AR_SECTOR, AR_SOURCE, AR_SCREENED_BY, AR_VERDICT, arCheckLabel, arCheckValue, arMap, arReason,
  AR_RULING,
} from "@/i18n_halal";
import { PublicHeader } from "@/components/PublicHeader";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, ArrowRight, ShieldCheck, AlertTriangle, Ban, CheckCircle2, XCircle, Share2,
  Gavel, Landmark, Download, Link2, MessageCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE = {
  "Compliant": { cls: "bg-emerald-500 text-white", soft: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: ShieldCheck },
  "Questionable": { cls: "bg-amber-500 text-white", soft: "text-amber-700 bg-amber-50 border-amber-200", Icon: AlertTriangle },
  "Non-Compliant": { cls: "bg-rose-500 text-white", soft: "text-rose-700 bg-rose-50 border-rose-200", Icon: Ban },
};

const cap = (v) => (!v ? "—" : v >= 1e12 ? `$${(v / 1e12).toFixed(2)}T` : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(0)}M`);

const Check = ({ label, detail, pass, testId }) => (
  <div className="flex items-start gap-3 rounded-2xl border border-border p-4" data-testid={`check-${testId}`}>
    {pass ? <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          : <XCircle className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />}
    <div>
      <div className="text-sm font-semibold">{label}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{detail}</div>
    </div>
  </div>
);

export default function HalalStock() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const ar = lang === "ar";
  const [d, setD] = useState(null);
  const [candles, setCandles] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    setD(null);
    axios.get(`${API}/public/halal-stock/${encodeURIComponent(symbol)}`)
      .then((r) => setD(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "We don't screen that instrument."));
    axios.get(`${API}/public/halal-stock/${encodeURIComponent(symbol)}/candles?days=120`)
      .then((r) => setCandles(r.data.candles)).catch(() => {});
  }, [symbol]);

  const vlabel = (s) => (ar ? arMap(AR_VERDICT, s) : verdictLabel(s));
  const [shareOpen, setShareOpen] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const cardUrl = `${API}/public/halal-stock/${encodeURIComponent(symbol)}/card.png`;
  const shareText = d
    ? `${d.instrument.symbol} (${d.instrument.name}) is ${verdictLabel(d.compliance.status)} on the AAOIFI screens — see the numbers: `
    : "";

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    toast.success(t("stk_link_copied"));
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="halal-stock-missing">
        <div className="text-center">
          <h1 className="text-2xl font-bold">{error}</h1>
          <Button className="mt-5 rounded-full" onClick={() => navigate("/top-halal-stocks")}>
            {t("stk_browse")}
          </Button>
        </div>
      </div>
    );
  }
  if (!d) return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">{t("stk_screening")} {symbol}…</div>;

  const c = d.compliance;
  const tone = TONE[c.status] || TONE.Questionable;
  const i = d.instrument;
  const q = d.quote;
  const series = candles.map((x) => ({ date: x.date, close: x.close }));
  const arRuling = d.ruling ? AR_RULING[d.ruling.slug] : null;

  return (
    <div className="min-h-screen bg-background" data-testid="halal-stock-page">
      <PublicHeader />
      <div className={`${tone.cls}`}>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-9 sm:py-12">
          <div className="mb-6 flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/top-halal-stocks")}
              className="text-white/80 hover:text-white hover:bg-white/15" data-testid="stock-back">
              <ArrowLeft className="h-4 w-4 me-1.5" />{t("stk_back")}
            </Button>
          </div>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] opacity-80">
                <tone.Icon className="h-3.5 w-3.5" />{vlabel(c.status)}
              </div>
              <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="stock-title">
                {i.symbol} <span className="opacity-70 font-semibold">· {i.name}</span>
              </h1>
              <p className="mt-2 text-sm opacity-80">
                {i.market} · {ar ? arMap(AR_SECTOR, i.sector) : i.sector} · {i.asset_class}
              </p>
            </div>
            <div className="text-end">
              <div className="numeric text-3xl font-bold">{fmtCcy(q.price, i.currency)}</div>
              <div className="numeric text-sm opacity-85">
                {q.change_pct >= 0 ? "+" : ""}{q.change_pct}% {t("stk_today")}
              </div>
              <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}
                className="mt-3 rounded-full bg-transparent border-white/40 text-white hover:bg-white/15 hover:text-white"
                data-testid="stock-share">
                <Share2 className="h-3.5 w-3.5 me-1.5" />{t("stk_share")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Card className="p-5 sm:p-6 shadow-premium">
            <h2 className="text-base md:text-lg font-semibold">{t("stk_why")}</h2>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {`${i.name.replace(/\.$/, "")} `}
              {c.status === "Compliant" ? t("stk_why_pass") : t("stk_why_fail")}
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {(c.checks || []).map((chk) => (
                <Check key={chk.label} testId={chk.label.split(" ")[0].toLowerCase()}
                  label={ar ? arCheckLabel(chk.label) : chk.label} pass={chk.pass}
                  detail={ar ? arCheckValue(chk.value) : chk.value} />
              ))}
            </div>
            {(c.reasons || []).length > 0 && (
              <ul className="mt-4 space-y-1.5" data-testid="stock-reasons">
                {c.reasons.map((r, k) => (
                  <li key={`${r}-${k}`} className={`flex gap-2 text-xs ${c.status === "Compliant" ? "text-emerald-700" : "text-rose-700"}`}>
                    {c.status === "Compliant"
                      ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      : <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}{ar ? arReason(r) : r}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
              <span>{t("stk_purification_factor")} <span className="numeric font-semibold text-foreground">{c.purification_pct || 0}%</span></span>
              <span>{t("stk_tax_at_source")} <span className="numeric font-semibold text-foreground">{d.withholding.rate_pct}%</span> ({d.withholding.country_name})</span>
              <span>{t("stk_source")}: {c.data_source
                ? (ar ? arMap(AR_SOURCE, c.data_source) : c.data_source)
                : t("stk_source_filings")}</span>
            </div>
          </Card>

          {series.length > 0 && (
            <Card className="p-5 sm:p-6 shadow-premium" data-testid="stock-chart">
              <h2 className="text-base md:text-lg font-semibold mb-3">{t("stk_last_120")}</h2>
              <LineTrend data={series} dataKey="close" height={260} />
            </Card>
          )}

          {d.ruling && (
            <Card className="p-5 sm:p-6 shadow-premium" data-testid="stock-ruling">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                <Gavel className="h-3.5 w-3.5" />{t("stk_ruling_eyebrow")}
              </div>
              <h2 className="mt-2.5 text-base md:text-lg font-semibold">
                {ar && arRuling ? arRuling.question : d.ruling.question}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground/80">
                {ar && arRuling ? arRuling.ruling : d.ruling.ruling}
              </p>
              <button onClick={() => navigate("/shariah-board")} className="mt-3 text-xs font-semibold text-[#3E2E65]"
                data-testid="stock-board-link">{t("stk_all_rulings")} <ArrowRight className="inline h-3 w-3" /></button>
            </Card>
          )}
        </div>

        <div className="space-y-5">
          <Card className="p-5 shadow-premium" data-testid="stock-facts">
            <h2 className="text-sm font-semibold mb-3">{t("stk_key_figures")}</h2>
            <div className="space-y-2 text-xs">
              {[[t("stk_mcap"), cap(q.market_cap)], [t("stk_open"), fmtCcy(q.open, i.currency)],
                [t("stk_high_52w"), fmtCcy(q.high_52w, i.currency)], [t("stk_low_52w"), fmtCcy(q.low_52w, i.currency)],
                [t("stk_volume"), (q.volume || 0).toLocaleString()]].map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-muted-foreground">{k}</span><span className="numeric font-semibold">{v}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5 shadow-premium">
            <div className="flex items-center gap-2 text-sm font-semibold mb-2">
              <Landmark className="h-4 w-4 text-[#3E2E65]" />{t("stk_buy_title")}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{t("stk_buy_sub")}</p>
            <Button className="w-full mt-4 rounded-full" onClick={() => navigate("/login")}
              data-testid="stock-cta">{t("stk_open_account")} <ArrowRight className="h-4 w-4 ms-1.5" /></Button>
          </Card>

          {d.peers.length > 0 && (
            <Card className="p-5 shadow-premium" data-testid="stock-peers">
              <h2 className="text-sm font-semibold mb-3">
                {t("stk_peers")} {ar ? arMap(AR_SECTOR, i.sector) : i.sector}
              </h2>
              <div className="space-y-1.5">
                {d.peers.map((p) => (
                  <button key={p.symbol} onClick={() => navigate(`/halal-stocks/${encodeURIComponent(p.symbol)}`)}
                    className="w-full flex items-center justify-between rounded-xl border border-border p-2.5 hover:bg-muted/50 transition-colors text-start"
                    data-testid={`peer-${p.symbol}`}>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold">{p.symbol}</div>
                      <div className="text-[10px] text-muted-foreground truncate max-w-[150px]">{p.name}</div>
                    </div>
                    <span className="text-[10px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">
                      {p.purification_pct}% {t("stk_purify")}
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle>{t("stk_share")}</DialogTitle>
          </DialogHeader>
          <img src={cardUrl} alt={`${symbol} Shariah verdict card`}
            className="w-full rounded-xl border border-border" data-testid="share-card-image" />
          <div className="grid sm:grid-cols-3 gap-2">
            <Button className="rounded-full" data-testid="share-whatsapp"
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + url)}`, "_blank")}>
              <MessageCircle className="h-4 w-4 me-1.5" />{t("stk_whatsapp")}
            </Button>
            <Button variant="outline" className="rounded-full" onClick={copyLink} data-testid="share-copy">
              <Link2 className="h-4 w-4 me-1.5" />{t("stk_copy")}
            </Button>
            <Button variant="outline" className="rounded-full" data-testid="share-download"
              onClick={() => window.open(cardUrl, "_blank")}>
              <Download className="h-4 w-4 me-1.5" />{t("stk_save_card")}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">{t("stk_share_card_note")}</p>
        </DialogContent>
      </Dialog>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 pb-14">
        <p className="text-xs text-muted-foreground leading-relaxed">
          {ar ? AR_SCREENED_BY : d.screened_by}. {t("stk_footnote")}
        </p>
      </div>
    </div>
  );
}
